import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Ban, CircleCheck, Mail, Pencil, Plus, Trash2, Users } from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { reportSchedule } from '../../lib/types'
import { nameSchema, emailSchema } from '../../lib/validators'

/**
 * Report Recipients — manage the per-tenant list of people who receive the
 * company's admin-facing report emails (the weekly / monthly missed-report
 * digests and the per-company usage digest). Recipients are notification
 * targets only: they get no software access.
 *
 * Storage: recipients are EMBEDDED on the ReportSchedule row, so this reuses
 * the existing schedule endpoints — it reads `reportRecipients` from
 * `GET /reports/config/schedule` and saves the FULL desired array back via
 * `PATCH /reports/config/schedule` (add / edit / enable-disable / remove all
 * resolve to "here is the new list"). Sharing the `['report-schedule']` query
 * key with the page means one cache; the schedule form's own Save omits
 * `reportRecipients` and never disturbs this list. `designation` is free text
 * — no hardcoded roles — and is for identification only.
 */

// Recipient Full Name + Email ID use the EXACT sign-up validators so the rules
// and wording match the Sign-Up page (and the Reminders & Schedule "Additional
// recipients"): Full Name 2–50 chars, letters/spaces/'-. only; strict Email ID
// (no spaces, no consecutive dots, strict TLD, ≤100 chars).
const RECIPIENT_NAME_SCHEMA = nameSchema('Full Name')
const RECIPIENT_EMAIL_SCHEMA = emailSchema({ strictTld: true })

/** Designation cap — mirrors `usageRecipientItem` in the API's platform.dto.js.
 *  The server is what blocks the save; this drives the live message so the
 *  admin is TOLD they are over, rather than finding the input silently
 *  refusing keystrokes (QA, 2026-09-01). Same soft-limit contract the
 *  shared TextField documents. */
const MAX = { designation: 120 }

const EMPTY_FORM = { fullName: '', email: '', designation: '', status: 'active' }

/** Strip a recipient down to the wire shape the schedule PATCH expects.
 *  Existing rows keep their `id` so the server can match them; a new row is
 *  sent without one and the server assigns it. */
const toWire = (r) => ({
  id: r.id,
  fullName: r.fullName,
  email: r.email,
  designation: r.designation ?? '',
  status: r.status,
})

/** Solid status pill — same shape used across Departments / Workspaces /
 *  Work Roles so every admin list reads consistently. */
function StatusBadge({ status }) {
  const active = status === 'active'
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white border border-transparent dark:border-white/10 ' +
        (active
          ? 'bg-[color:var(--color-success)] dark:bg-white/[0.06] dark:text-emerald-300'
          : 'bg-[color:var(--color-priority-urgent)] dark:bg-white/[0.06] dark:text-rose-300')
      }
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  )
}

const FIELD =
  'w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-accent)] focus:ring-2 focus:ring-[color:var(--color-accent)]/20'

function RecipientForm({ form, setForm, errors }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-[color:var(--color-ink-strong)] mb-1">
          Full Name <span className="text-[color:var(--color-priority-urgent)]">*</span>
        </label>
        <input
          type="text"
          value={form.fullName}
          onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
          className={FIELD}
          placeholder="Enter the Full Name"
        />
        {errors.fullName ? (
          <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">{errors.fullName}</p>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium text-[color:var(--color-ink-strong)] mb-1">
          Email ID <span className="text-[color:var(--color-priority-urgent)]">*</span>
        </label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          className={FIELD}
          placeholder="Enter the Email ID"
        />
        {errors.email ? (
          <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">{errors.email}</p>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium text-[color:var(--color-ink-strong)] mb-1">
          Designation
        </label>
        <input
          type="text"
          value={form.designation}
          onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
          className={FIELD}
          placeholder="Enter a Designation"
        />
        {errors.designation ? (
          <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">{errors.designation}</p>
        ) : null}
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
          Any job title (e.g. CEO, HR Head, Operations Manager) — for
          identification only. It does not grant any access.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-[color:var(--color-ink-strong)] mb-1">
          Status
        </label>
        <div className="flex items-center gap-2">
          {['active', 'inactive'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setForm((f) => ({ ...f, status: s }))}
              className={
                'px-3 py-1.5 rounded-md text-sm font-medium border cursor-pointer transition-colors ' +
                (form.status === s
                  ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)]'
                  : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink)] border-[color:var(--color-rule)] hover:border-[color:var(--color-rule-strong)]')
              }
            >
              {s === 'active' ? 'Active' : 'Inactive'}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
          Inactive recipients are kept but do not receive reports.
        </p>
      </div>
    </div>
  )
}

/**
 * Report Recipients — reusable across the Company Admin (per-tenant schedule)
 * and Master Admin (platform Usage Report config) pages. By default it reads
 * and writes the tenant's schedule row; pass `queryKey` + `loadRecipients` +
 * `saveRecipients` to back it with a different source (e.g. the platform
 * config). `description` / `emptyBody` override the copy.
 */
export function ReportRecipientsSection({
  queryKey = ['report-schedule'],
  loadRecipients,
  saveRecipients,
  description,
  emptyBody,
} = {}) {
  const qc = useQueryClient()
  const [dialog, setDialog] = useState(null) // { mode: 'add'|'edit', recipient? }
  const [form, setForm] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [deleteTarget, setDeleteTarget] = useState(null)

  // Default source = the tenant's schedule row (recipients embedded there).
  // Callers override with loadRecipients/saveRecipients to point elsewhere.
  const listQuery = useQuery({
    queryKey,
    queryFn:
      loadRecipients ??
      (async () => {
        const res = await api.get('/reports/config/schedule')
        return reportSchedule.parse(res.data).reportRecipients ?? []
      }),
  })
  // Defensive: `?? []` only guards null/undefined — a transient non-array
  // payload (mid-refetch / unexpected shape) would make `.filter`/`.map`
  // throw and white-screen the page via the ErrorBoundary. Coerce to array.
  const recipients = Array.isArray(listQuery.data) ? listQuery.data : []
  const activeCount = useMemo(
    () => recipients.filter((r) => r.status === 'active').length,
    [recipients],
  )

  // Client-side duplicate guard (server enforces it too) so the user gets an
  // inline message instead of a round-trip. Case-insensitive, tenant-scoped
  // by construction (the list is this tenant's).
  function emailExists(email, excludeId) {
    const e = email.trim().toLowerCase()
    return recipients.some((r) => r.email.toLowerCase() === e && r.id !== excludeId)
  }

  function validate(f, excludeId) {
    const errs = {}
    // Full Name + Email ID: the exact sign-up rules (see schema constants).
    const nameRes = RECIPIENT_NAME_SCHEMA.safeParse(f.fullName)
    if (!nameRes.success) errs.fullName = nameRes.error.issues[0]?.message
    const emailRes = RECIPIENT_EMAIL_SCHEMA.safeParse(f.email)
    if (!emailRes.success) errs.email = emailRes.error.issues[0]?.message
    // Duplicate guard runs only once the address itself is valid.
    else if (emailExists(f.email, excludeId))
      errs.email = 'A recipient with this Email ID already exists.'
    if (f.designation.trim().length > MAX.designation)
      errs.designation = `Designation must be at most ${MAX.designation} characters.`
    return errs
  }

  function openAdd() {
    setForm(EMPTY_FORM)
    setErrors({})
    setDialog({ mode: 'add' })
  }
  function openEdit(r) {
    setForm({
      fullName: r.fullName,
      email: r.email,
      designation: r.designation ?? '',
      status: r.status,
    })
    setErrors({})
    setDialog({ mode: 'edit', recipient: r })
  }
  function closeDialog() {
    setDialog(null)
  }

  // ONE mutation for every action — each sends the full desired array to the
  // configured save target (tenant schedule PATCH by default).
  const saveMutation = useMutation({
    mutationFn: async ({ recipients: next }) => {
      if (saveRecipients) return saveRecipients(next)
      await api.patch('/reports/config/schedule', { reportRecipients: next })
    },
    onSuccess: (_data, vars) => {
      toast.success(vars.message)
      void qc.invalidateQueries({ queryKey })
      if (vars.closeDialog) closeDialog()
      if (vars.clearDelete) setDeleteTarget(null)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function submit() {
    const excludeId = dialog?.recipient?.id
    const errs = validate(form, excludeId)
    setErrors(errs)
    if (Object.keys(errs).length > 0) return
    const item = {
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      designation: form.designation.trim(),
      status: form.status,
    }
    const next =
      dialog.mode === 'edit'
        ? recipients.map((r) => (r.id === excludeId ? { id: r.id, ...item } : toWire(r)))
        : [...recipients.map(toWire), item]
    saveMutation.mutate({
      recipients: next,
      message:
        dialog.mode === 'edit'
          ? 'Recipient updated successfully.'
          : 'Recipient added successfully.',
      closeDialog: true,
    })
  }

  function toggle(r) {
    const next = recipients.map((x) =>
      x.id === r.id
        ? { ...toWire(x), status: x.status === 'active' ? 'inactive' : 'active' }
        : toWire(x),
    )
    saveMutation.mutate({
      recipients: next,
      message:
        r.status === 'active'
          ? 'Recipient disabled successfully.'
          : 'Recipient enabled successfully.',
    })
  }

  function remove(r) {
    const next = recipients.filter((x) => x.id !== r.id).map(toWire)
    saveMutation.mutate({
      recipients: next,
      message: 'Recipient removed successfully.',
      clearDelete: true,
    })
  }

  return (
    <section className="mb-5 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0">
          <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 flex items-center gap-2">
            <Users size={18} className="text-[color:var(--color-accent-strong)]" />
            Report Recipients
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            {description ??
              "People who receive the company's Usage Report email — a periodic summary of activity (active users, tasks, AI usage and emails sent). Recipients get the report by email only — no access to the software. When none are added, the Usage Report goes to the company administrators as before."}
          </p>
        </div>
        <Button size="sm" onClick={openAdd} className="shrink-0">
          <Plus size={14} />
          Add Recipient
        </Button>
      </div>

      {listQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center">
          Loading recipients…
        </p>
      ) : listQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)] py-6 text-center">
          {extractApiError(listQuery.error).message}
        </p>
      ) : recipients.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-canvas)] px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] mb-2">
            <Mail size={18} />
          </div>
          <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
            No recipients yet
          </p>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            {emptyBody ??
              'The Usage Report currently goes to the company administrators. Add a recipient to send it elsewhere too.'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm responsive-table">
                <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
                  <tr>
                    <th className="px-4 py-3 font-medium text-sm w-12">SR. No.</th>
                    <th className="px-4 py-3 font-medium text-sm">Full Name</th>
                    <th className="px-4 py-3 font-medium text-sm">Email ID</th>
                    <th className="px-4 py-3 font-medium text-sm">Designation</th>
                    <th className="px-4 py-3 font-medium text-sm w-28">Status</th>
                    <th className="px-4 py-3 font-medium text-sm w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((r, idx) => (
                    <tr
                      key={r.id}
                      className="border-t border-[color:var(--color-rule)] hover:bg-[color:var(--color-canvas)]"
                    >
                      <td data-label="SR. No." className="px-4 py-3 align-middle text-[color:var(--color-ink-muted)]">
                        {idx + 1}
                      </td>
                      <td data-label="Full Name" className="px-4 py-3 align-middle text-[color:var(--color-ink-strong)] font-medium">
                        {r.fullName}
                      </td>
                      <td data-label="Email ID" className="px-4 py-3 align-middle text-[color:var(--color-ink)]">
                        {r.email}
                      </td>
                      <td data-label="Designation" className="px-4 py-3 align-middle text-[color:var(--color-ink)]">
                        {r.designation ? (
                          r.designation
                        ) : (
                          <span className="text-[color:var(--color-ink-muted)]">—</span>
                        )}
                      </td>
                      <td data-label="Status" className="px-4 py-3 align-middle">
                        <StatusBadge status={r.status} />
                      </td>
                      <td data-label="Actions" className="px-4 py-3 align-middle">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(r)}
                            className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors"
                            title="Edit"
                            aria-label="Edit recipient"
                          >
                            <Pencil size={18} strokeWidth={1.5} />
                          </button>
                          <button
                            type="button"
                            onClick={() => toggle(r)}
                            disabled={saveMutation.isPending}
                            className={
                              'p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed ' +
                              (r.status === 'active'
                                ? 'hover:text-[color:var(--color-priority-urgent)]'
                                : 'hover:text-[color:var(--color-success)]')
                            }
                            title={r.status === 'active' ? 'Disable recipient' : 'Enable recipient'}
                            aria-label={r.status === 'active' ? 'Disable recipient' : 'Enable recipient'}
                          >
                            {r.status === 'active' ? (
                              <Ban size={18} strokeWidth={1.5} />
                            ) : (
                              <CircleCheck size={18} strokeWidth={1.5} />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(r)}
                            className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors"
                            title="Delete"
                            aria-label="Remove recipient"
                          >
                            <Trash2 size={18} strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-2 text-xs text-[color:var(--color-ink-muted)]">
            {recipients.length} recipient{recipients.length === 1 ? '' : 's'} ·{' '}
            {activeCount} active
          </p>
        </>
      )}

      {/* Add / Edit dialog */}
      <Modal
        open={Boolean(dialog)}
        onClose={closeDialog}
        title={dialog?.mode === 'edit' ? 'Edit Recipient' : 'Add Recipient'}
        description="Recipients receive the Usage Report only — they get no access to the software."
        footer={
          <>
            <Button variant="secondary" onClick={closeDialog} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saveMutation.isPending}>
              {saveMutation.isPending
                ? 'Saving…'
                : dialog?.mode === 'edit'
                  ? 'Save Changes'
                  : 'Add Recipient'}
            </Button>
          </>
        }
      >
        <RecipientForm form={form} setForm={setForm} errors={errors} />
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Remove Recipient?"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => remove(deleteTarget)}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Removing…' : 'Remove'}
            </Button>
          </>
        }
      >
        {/* `break-words`/`overflow-wrap:anywhere` — the recipient's name and
          *  Email ID are interpolated here, and a long space-less name has no
          *  break opportunity, so the paragraph overflowed and the Modal body
          *  grew a horizontal scrollbar (QA, 2026-09-01). This is a raw
          *  <Modal>, not <ConfirmDialog>, so it never inherited the wrap rule
          *  that component already applies to its own message. */}
        <p className="text-sm text-[color:var(--color-ink)] break-words [overflow-wrap:anywhere]">
          {deleteTarget
            ? `${deleteTarget.fullName} (${deleteTarget.email}) will stop receiving the Usage Report. You can add them again later.`
            : ''}
        </p>
      </Modal>
    </section>
  )
}
